export const API_BASE_URL = '/api';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.map(cb => cb(token));
  refreshSubscribers = [];
};

const refreshToken = async () => {
  const response = await fetch(`${API_BASE_URL}/v2/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
  });

  if (!response.ok) {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_logged_in');
    window.location.href = '/login';
    throw new Error('Refresh failed');
  }

  const data = await response.json();
  return data.access_token || 'authenticated';
};

export const handleResponse = async response => {
  if (!response.ok) {
    let errorData = { message: `HTTP error! status: ${response.status}` };
    try {
      errorData = await response.json();
    } catch (e) {
      // If JSON parsing fails, use default error
    }
    const error = new Error(
      errorData.detail ||
        errorData.message ||
        `Request failed with status ${response.status}`,
    );
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  return response.json();
};

const request = async (url: string, options: any = {}) => {
  const headers = {
    ...options.headers,
  };

  let response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    credentials: 'same-origin',
    headers,
  });

  if (response.status === 401 && localStorage.getItem('is_logged_in') === 'true') {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const newToken = await refreshToken();
        isRefreshing = false;
        onTokenRefreshed(newToken);
      } catch (error) {
        isRefreshing = false;
        throw error;
      }
    }

    return new Promise(resolve => {
      subscribeTokenRefresh(async (token: string) => {
        const retryHeaders = {
          ...options.headers,
        };
        const retryResponse = await fetch(`${API_BASE_URL}${url}`, {
          ...options,
          credentials: 'same-origin',
          headers: retryHeaders,
        });
        resolve(handleResponse(retryResponse));
      });
    });
  }

  return handleResponse(response);
};

export const apiClient = {
  get: (url: string) => request(url, { method: 'GET' }),
  post: (url: string, data: any) =>
    request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  postForm: (url: string, formData: FormData) =>
    request(url, {
      method: 'POST',
      body: formData,
    }),
  delete: (url: string) => request(url, { method: 'DELETE' }),
  patch: (url: string, data: any) =>
    request(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  put: (url: string, data: any) =>
    request(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
};

// TypeScript types for API requests/responses
export interface IdeaGenerationSurveyCreate {
  goal: string;
  audience: string;
  audience_size: string;
  author_skills: string;
  knowledge_level: string;
}

export interface IdeaGenerationSurveyResponse extends IdeaGenerationSurveyCreate {
  id: string;
  user_id: string;
  created_at: string;
}

export interface GeneratedIdeasResponse {
  survey_id: string;
  ideas: string[];
  generated_ideas_id: string;
}

export interface ExpandedIdeaResponse {
  idea_id: string;
  title: string;
  structure: string[];
  key_points: string[];
  raw?: any;
}

export interface ExpandIdeaRequest {
  survey_id: string;
  idea_id?: string;
  idea_text?: string;
}

export interface HistoryEntry {
  history_id: string;
  performance_id: number | null;
  analysis_type: string;
  created_at: string;
  result_payload: Record<string, unknown>;
}

// Feature Toggle types
export interface FeatureToggleResponse {
  feature: string;
  enabled: boolean;
}

export interface AllFeatureTogglesResponse {
  features: Record<string, boolean>;
}

// Presentation Generation types
export interface GeneratePresentationRequest {
  idea_id: string;
  theme?: string;
  generate_images?: boolean;
  num_slides?: number;
  language?: string;
}

export interface GeneratePresentationResponse {
  job_id: string;
  status: string;
  pptx_url?: string;
  slides_count?: number;
  images_generated?: number;
}

// Typed API methods
export const api = {
  // Idea generation endpoints
  createIdeaSurvey: (
    payload: IdeaGenerationSurveyCreate,
  ): Promise<IdeaGenerationSurveyResponse> => apiClient.post('/v2/idea-survey', payload),

  generateIdeas: (surveyId: string): Promise<GeneratedIdeasResponse> =>
    apiClient.post('/v2/generate-ideas', { survey_id: surveyId }),

  expandIdea: (request: ExpandIdeaRequest): Promise<ExpandedIdeaResponse> =>
    apiClient.post('/v2/expand-idea', request),

  getGeneratedIdeas: (surveyId: string): Promise<GeneratedIdeasResponse> =>
    apiClient.get(`/v2/generated-ideas/${surveyId}`),

  getExpandedIdea: (ideaId: string): Promise<ExpandedIdeaResponse> =>
    apiClient.get(`/v2/expanded-idea/${ideaId}`),

  // History endpoints
  getHistory: (): Promise<HistoryEntry[]> => apiClient.get('/v2/history'),

  // Auth endpoints
  logout: (): Promise<void> => apiClient.post('/v2/logout', {}),

  // Feature Toggles
  getFeatureToggle: (featureName: string): Promise<FeatureToggleResponse> =>
    apiClient.get(`/v2/features/${featureName}`),

  getAllFeatureToggles: (): Promise<AllFeatureTogglesResponse> =>
    apiClient.get('/v2/features'),

  // Presentation Generation
  generatePresentation: (
    ideaId: string,
    request: GeneratePresentationRequest,
  ): Promise<GeneratePresentationResponse> =>
    apiClient.post(`/v2/ideas/${ideaId}/generate-presentation`, request),
};
